import { AxiosError } from "axios";
import { appContext } from "../../../AppContext";
import { IbssComponent } from "../../Core/BaseComponent/IbssComponent";
import { JSX } from "react";
import { AlertDialog, Button, ButtonBar, Card, Dialog, DialogContent, DialogFooter, GridContainer, GridItem, HelperText, PasswordField, RightButtons } from "@iconics/components";

export class PasswordDialog extends IbssComponent<IProps, IState>
{
    constructor(props: IProps)
    {
        super(props);
        this.state = {
            oldPassword: '',
            oldPasswordError: '',
            newPassword: '',
            newPasswordError: '',
            retypePassword: '',
            retypePasswordError: '',
            error: '',
            showSuccess: false,
        }
    }

    public componentDidUpdate(prevProps: IProps): void
    {
        if (prevProps.open != this.props.open && this.props.open)
        {
            this.handleOpen();
        }
    }

    private validate(key?: keyof IState): void
    {
        const { labels } = appContext();
        const { oldPassword, newPassword, retypePassword, error } = this.pendingState;

        if (!key || key == 'oldPassword')
        {
            this.setState({ oldPasswordError: (!oldPassword ? labels.Platform_Shared_Required : '') });
        }
        if (!key || key == 'newPassword')
        {
            this.setState({ newPasswordError: (!newPassword ? labels.Platform_Shared_Required : '') });
        }
        if (!key || key == 'retypePassword')
        {
            this.setState({ retypePasswordError: (!retypePassword ? labels.Platform_Shared_Required : '') });
        }
        if ((!key || key == 'newPassword' || key == 'retypePassword') && !!newPassword && !!retypePassword)
        {
            this.setState({ retypePasswordError: (newPassword != retypePassword ? labels.Platform_ChangePassword_PasswordsMustBeEqual : '') });
        }
    }

    private get isValid(): boolean
    {
        const { oldPassword, newPassword, retypePassword, error } = this.pendingState;
        return (!!oldPassword && !!newPassword && !!retypePassword && newPassword == retypePassword && !error);
    }

    private handleOpen(): void
    {
        this.setState({
            oldPassword: '',
            oldPasswordError: '',
            newPassword: '',
            newPasswordError: '',
            retypePassword: '',
            retypePasswordError: '',
            error: '',
            showSuccess: false,
        })
    }

    private async handleSubmit(): Promise<void>
    {
        const { localStorageProvider, labels } = appContext();
        try
        {
            this.validate();
            if (!this.isValid)
            {
                return;
            }

            const payload = {
                email: localStorageProvider.getUserDetails().email,
                oldPassword: this.state.oldPassword,
                newPassword: this.state.newPassword,
            }

            await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.resetpassword.post<string>({
                tenancy: appContext().tenantId,
                body: {
                    Email: payload.email,
                    OldPassword: payload.oldPassword,
                    NewPassword: payload.newPassword
                }
            });
            this.setState({ showSuccess: true });
        }
        catch (error: any)
        {
            const axiosError = error as AxiosError<{ message: string }>;
            const errorMessage = (axiosError?.response?.data?.message ?? labels.Platform_Common_SomethingWentWrong);
            this.setState({ error: errorMessage });
        }
    }

    private handleSuccessDialogClose(): void
    {
        this.setState({ showSuccess: false });
        this.props.onClose();
    }

    public render(): JSX.Element
    {
        const { labels } = appContext();

        return (
            <>
                <Dialog
                    isOpen={this.props.open}
                    onOpenChange={() => this.props.onClose()}
                    width={4}
                >
                    <DialogContent>
                        <Card
                            title={labels.Platform_ChangePassword_Title}
                            subTitle={labels.Platform_ChangePassword_SubTitle}
                        >
                            <GridContainer>
                                {!!this.state.error &&
                                    <GridItem>
                                        <HelperText error>{this.state.error}</HelperText>
                                    </GridItem>
                                }
                                <GridItem>
                                    <PasswordField
                                        value={this.state.oldPassword}
                                        onChange={e => this.setState({ oldPassword: e.target.value, error: '' }, () => this.validate('oldPassword'))}
                                        label={labels.Platform_ChangePassword_Original}
                                        placeholder={labels.Platform_ChangePassword_Original}
                                        invalid={!!this.state.oldPasswordError}
                                        invalidMessage={this.state.oldPasswordError}
                                        required={true}
                                    />
                                </GridItem>
                                <GridItem>
                                    <PasswordField
                                        value={this.state.newPassword}
                                        onChange={e => this.setState({ newPassword: e.target.value, error: '' }, () => this.validate('newPassword'))}
                                        label={labels.Platform_ChangePassword_New1}
                                        placeholder={labels.Platform_ChangePassword_New1}
                                        invalid={!!this.state.newPasswordError}
                                        invalidMessage={this.state.newPasswordError}
                                        autoComplete="new-password"
                                        required={true}
                                    />
                                </GridItem>
                                <GridItem>
                                    <PasswordField
                                        value={this.state.retypePassword}
                                        onChange={e => this.setState({ retypePassword: e.target.value, error: '' }, () => this.validate('retypePassword'))}
                                        label={labels.Platform_ChangePassword_New2}
                                        placeholder={labels.Platform_ChangePassword_New2}
                                        invalid={!!this.state.retypePasswordError}
                                        invalidMessage={this.state.retypePasswordError}
                                        autoComplete="new-password"
                                        required={true}
                                    />
                                </GridItem>
                            </GridContainer>
                        </Card>
                    </DialogContent>
                    <DialogFooter>
                        <ButtonBar>
                            <RightButtons>
                                <>
                                    <Button
                                        styleType='outlined'
                                        onClick={() => this.props.onClose()}
                                    >
                                        {labels.Platform_CommonAction_Cancel}
                                    </Button>
                                    <Button
                                        styleType='filled'
                                        onClick={() => this.handleSubmit()}
                                        disabled={!this.isValid}
                                    >
                                        {labels.Platform_CommonAction_Save}
                                    </Button>
                                </>
                            </RightButtons>
                        </ButtonBar>
                    </DialogFooter>
                </Dialog>
                <AlertDialog
                    show={this.state.showSuccess}
                    title={labels.Platform_Common_Success}
                    message={labels.Platform_ChangePassword_Success}
                    onClose={() => this.handleSuccessDialogClose()}
                />
            </>
        );
    }
}

export interface IProps
{
    open: boolean;
    onClose: () => void;
}

export interface IState
{
    oldPassword: string;
    oldPasswordError: string;
    newPassword: string;
    newPasswordError: string;
    retypePassword: string;
    retypePasswordError: string;
    error: string;
    showSuccess: boolean;
}
